import { useForm, zodResolver } from '@mantine/form';
import { useTranslation } from 'react-i18next';
import {
  ErrorPage,
  PageContainer,
  PageFooter,
  PageHeader,
  SimpleStepDots,
  StepProps,
} from '../../../shared_components';
import { Flex, PasswordInput, Space, Stack } from '@mantine/core';
import EastIcon from '@mui/icons-material/East';

import { useState } from 'react';
import { password, Password, useUserDataStore } from '../../../shared_stores';
import classes from '../../../app.module.css';
import { PasswordRequirement } from './PasswordRequirementLabel';

export const StepPassword = ({
  forward,
  back,
  step = 0,
  max = 0,
}: StepProps) => {
  const { t } = useTranslation(['registration', 'errors', 'common']);
  const [isValid, setIsValid] = useState(false);
  const [passwordValue, setPasswordValue] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const userDataStore = useUserDataStore();
  const form = useForm<Password>({
    validateInputOnBlur: true,
    validate: zodResolver(password),
  });

  const requirements = [
    { re: /.{8,}/, label: t('req_length') },
    { re: /[0-9]/, label: t('req_numbers') },
    { re: /[A-Z]/, label: t('req_uppercase') },
    { re: /[a-z]/, label: t('req_lowercase') },
  ];

  const checks = requirements.map((requirement, index) => (
    <PasswordRequirement
      key={index}
      label={requirement.label}
      meets={requirement.re.test(passwordValue)}
    />
  ));

  const handleChange = () => {
    if (form.isDirty()) {
      userDataStore.setStoreDirty(true);
    }
    setIsValid(true);
  };

  const handleSubmit = () => {
    userDataStore.setPassword(form.getValues());
    void userDataStore.registerUser();
    if (!userDataStore.registrationError) {
      forward();
    }
  };

  if (userDataStore.registrationError) {
    return <ErrorPage message={t('errors:registration_error_message')} />;
  }

  return (
    <PageContainer>
      <form
        onChange={handleChange}
        onSubmit={form.onSubmit(handleSubmit, console.log)}
      >
        <Flex direction={'column'} justify={'space-between'} h={'100%'}>
          <Stack classNames={{ root: classes.footerPadding }}>
            <PageHeader
              title={t('password_title')}
              message={t('password_message')}
              topElement={<SimpleStepDots step={step} max={max} text={false} />}
            />
            <Stack gap={2}>
              <PasswordInput
                placeholder={t('password')}
                key={form.key('password')}
                value={passwordValue}
                onChange={(event) => {
                  setPasswordValue(event.currentTarget.value);
                  form.setFieldValue('password', event.currentTarget.value);
                }}
              />
              <Space h={4} />
              {checks}
            </Stack>
            <PasswordInput
              placeholder={t('repeat_password')}
              key={form.key('repeat_password')}
              onChange={(event) => setRepeatPassword(event.currentTarget.value)}
            />
            <Space h={'var(--mantine-spacing-xs)'} />
          </Stack>
          <PageFooter
            leftOnClick={back}
            buttonText={t('continue')}
            buttonIcon={<EastIcon />}
            submitButton={true}
            disabled={!isValid || passwordValue !== repeatPassword}
            loading={userDataStore.registeringUser}
          />
        </Flex>
      </form>
    </PageContainer>
  );
};
